import { useEffect, useState } from 'react';
import { shopify, SHOPIFY_ENABLED, GET_PRODUCTS, GET_PRODUCT_BY_HANDLE, normalizeProduct } from '../lib/shopify';

import { API } from '../utils/apiBase';

// Map a MongoDB product doc into the shape the rest of the UI expects (the
// same shape `normalizeProduct` returns for a Shopify product).
const fromDb = (p) => {
  const primary = p.media?.primary_image;
  const gallery = p.media?.gallery_images || [];
  // Build the ordered images array: primary first, then any gallery images that
  // aren't already the primary. ProductDetail.jsx renders thumbnails from this.
  const seen = new Set();
  const images = [];
  for (const url of [primary, ...gallery]) {
    if (url && !seen.has(url)) { seen.add(url); images.push(url); }
  }
  return {
    ...p,
    handle: p.id,
    slug: p.id,
    images,
    variants: (p.sizes || []).map((s) => ({ id: `${p.id}-${s}`, title: s, available: p.status !== 'sold-out' })),
    preorder: p.status === 'preorder',
    availableForSale: p.status === 'available' || p.status === 'preorder',
    soldOut: p.status === 'sold-out',
    comingSoon: p.status === 'coming-soon',
  };
};

export const useProducts = (opts = {}) => {
  const { query = null, first = 24 } = opts;
  const [data, setData] = useState({ products: [], loading: true, error: null });

  useEffect(() => {
    let active = true;
    setData((d) => ({ ...d, loading: true, error: null }));

    // Native path — pull from our local DB
    if (!SHOPIFY_ENABLED) {
      // Cache-bust + no-store so admin edits show up immediately, not after
      // browser/CDN cache expiry.
      const bust = Date.now();
      fetch(`${API}/api/products?_=${bust}`, { cache: 'no-store' })
        .then((r) => r.json())
        .then((d) => {
          if (!active) return;
          let items = (d.items || []).map(fromDb);
          if (query) {
            const q = String(query).toLowerCase();
            items = items.filter((p) => p.name.toLowerCase().includes(q));
          }
          setData({ products: items.slice(0, first), loading: false, error: null });
        })
        .catch((e) => active && setData({ products: [], loading: false, error: e.message || 'Failed to load products' }));
      return () => { active = false; };
    }

    // Legacy Shopify path — left intact in case the user re-enables it later
    shopify
      .request(GET_PRODUCTS, { first, query })
      .then((res) => {
        if (!active) return;
        const products = (res?.products?.edges || [])
          .map(({ node }) => normalizeProduct(node))
          .filter(Boolean);
        setData({ products, loading: false, error: null });
      })
      .catch((e) => {
        if (!active) return;
        if (process.env.NODE_ENV !== 'production') console.error('useProducts failed', e);
        setData({ products: [], loading: false, error: e.message || 'Failed to load products' });
      });
    return () => { active = false; };
  }, [query, first]);

  return data;
};

export const useProduct = (handle) => {
  const [data, setData] = useState({ product: null, loading: true, error: null });

  useEffect(() => {
    if (!handle) {
      setData({ product: null, loading: false, error: null });
      return;
    }
    let active = true;
    setData({ product: null, loading: true, error: null });

    if (!SHOPIFY_ENABLED) {
      const bust = Date.now();
      fetch(`${API}/api/products/${handle}?_=${bust}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Not found'))))
        .then((p) => active && setData({ product: fromDb(p), loading: false, error: null }))
        .catch((e) => active && setData({ product: null, loading: false, error: e.message || 'Failed to load product' }));
      return () => { active = false; };
    }

    shopify
      .request(GET_PRODUCT_BY_HANDLE, { handle })
      .then((res) => {
        if (!active) return;
        setData({ product: normalizeProduct(res?.product), loading: false, error: null });
      })
      .catch((e) => {
        if (!active) return;
        if (process.env.NODE_ENV !== 'production') console.error('useProduct failed', e);
        setData({ product: null, loading: false, error: e.message || 'Failed to load product' });
      });
    return () => { active = false; };
  }, [handle]);

  return data;
};
